import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { money } from '../events/util';

// MI PLAN: QUÉ PAGO, POR QUÉ, Y CÓMO LO CAMBIO.
//
// Lo central es "Lo que viene": el dueño tiene que saber cuánto va a pagar antes de que se
// emita el cargo, no enterarse cuando le llega. El resto es lo de siempre de una cuenta:
// qué debe, cómo pagarlo (Mercado Pago), qué pagó, y la comparativa para cambiar de plan.

const ORDEN = ['basico', 'pro', 'full'];
const NOMBRE = { basico: 'Básico', pro: 'Pro', full: 'Full' };
const fecha = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Argentina/Buenos_Aires' }) : '';
const fechaCorta = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' }) : '';

const card = { padding: '20px 22px', marginBottom: 18 };
const fila = { display: 'flex', alignItems: 'baseline', gap: 12, padding: '10px 0', borderTop: '1px solid var(--line)' };

// Precio con la bonificación a la vista: tachado el lleno, al lado lo que se paga.
function Importe({ bruto, neto, pct }) {
  if (!pct) return <b style={{ color: '#fff', whiteSpace: 'nowrap' }}>{money(neto)}</b>;
  return (
    <span style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
      <span style={{ color: 'var(--ink-600)', textDecoration: 'line-through', fontSize: 12, marginRight: 6 }}>{money(bruto)}</span>
      <b style={{ color: '#2ecc71' }}>{money(neto)}</b>
    </span>
  );
}

export default function MiPlan({ onCambio }) {
  const api = useApi();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);   // plan que se está cambiando, o id de cargo
  const [msg, setMsg] = useState('');
  const [pago, setPago] = useState(null);   // { estado, total } al volver de Mercado Pago

  const load = useCallback(() => {
    api.get('/api/billing').then(setD).catch(e => setErr(e.message));
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  // VUELTA DE MERCADO PAGO: la URL trae ?pago=<ref>. Se pregunta cómo quedó; si MP todavía
  // no confirmó, se vuelve a preguntar unas veces (el servidor le consulta a MP cada vez).
  useEffect(() => {
    const url = new URL(window.location.href);
    const ref = url.searchParams.get('pago');
    if (!ref) return;
    url.searchParams.delete('pago');
    window.history.replaceState(null, '', url.pathname + url.search);
    let vivo = true;
    setPago({ estado: 'consultando' });
    (async () => {
      for (let i = 0; i < 8 && vivo; i++) {
        try {
          const r = await api.get(`/api/billing/pago/${encodeURIComponent(ref)}`);
          if (!vivo) return;
          setPago(r);
          if (r.estado !== 'pendiente') { if (r.estado === 'aprobado') { load(); if (onCambio) onCambio(); } return; }
        } catch (e) { if (vivo) setPago({ estado: 'error', error: e.message }); return; }
        await new Promise(res => setTimeout(res, 4000));
      }
    })();
    return () => { vivo = false; };
  }, []); // eslint-disable-line

  if (!d) return <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>{err || 'Cargando…'}</div>;

  const actual = d.plan.actual;
  const pendiente = d.plan.pendiente;
  const planDe = (k) => d.planes.find(p => p.key === k);
  const pendientes = d.cargos.filter(c => c.status === 'unpaid');
  const historial = d.cargos.filter(c => c.status !== 'unpaid');

  // Qué deja de tener al bajar: las funciones de su plan que el nuevo no incluye.
  const pierde = (nuevo) => Object.values(d.funciones)
    .filter(f => ORDEN.indexOf(f.requiere) > ORDEN.indexOf(nuevo) && ORDEN.indexOf(f.requiere) <= ORDEN.indexOf(actual))
    .map(f => f.etiqueta);

  async function cambiar(nuevo) {
    const p = planDe(nuevo);
    let texto;
    if (d.plan.en_prueba) {
      texto = `Elegís el plan ${p.nombre}.\n\nTermina tu prueba gratis y empezás a pagar desde hoy` +
        (p.abono ? `: ${money(p.abono)} por mes más ${money(d.precio_entrada)} por entrada.` : `: ${money(d.precio_entrada)} por entrada, sin abono.`);
    } else if (nuevo === actual) {
      texto = `Cancelás el cambio a ${NOMBRE[pendiente.plan]} y seguís en ${NOMBRE[actual]}.`;
    } else if (ORDEN.indexOf(nuevo) > ORDEN.indexOf(actual)) {
      texto = `Pasás a ${p.nombre} ahora mismo.\n\n` +
        (d.abono ? `Lo que va del período en ${NOMBRE[actual]} se cobra proporcional a los días, y ` : '') +
        (p.abono ? `el abono de ${p.nombre} (${money(p.abono)} por mes) arranca hoy.` : '');
    } else {
      const pe = pierde(nuevo);
      texto = (d.abono ? `Seguís en ${NOMBRE[actual]} hasta el ${fecha(d.abono.hasta)}, que es lo que ya estás pagando. Desde ese día pasás a ${p.nombre}.` : `Pasás a ${p.nombre}.`) +
        (pe.length ? `\n\nDejás de tener:\n• ${pe.join('\n• ')}\n\nTus datos no se borran: si volvés a subir, está todo como lo dejaste.` : '');
    }
    if (!window.confirm(texto)) return;
    setBusy(nuevo); setMsg('');
    try {
      const r = await api.post('/api/billing/plan', { plan: nuevo });
      setMsg(r.tipo === 'programado' ? `Listo: pasás a ${NOMBRE[r.plan]} el ${fecha(r.desde)}.`
        : r.tipo === 'cancelado' ? 'Listo: cancelaste el cambio.'
        : `Listo: ya estás en ${NOMBRE[r.plan]}.`);
      load();
      if (onCambio) onCambio();
    } catch (e) { alert(e.message); } finally { setBusy(null); }
  }

  async function pagarConMp() {
    setBusy('mp');
    try {
      const r = await api.post('/api/billing/pagar', {});
      window.location.href = r.url;
    } catch (e) { alert(e.message); setBusy(null); }
  }

  return (
    <div className="lb-fade" style={{ maxWidth: 860 }}>
      {msg && <div style={{ marginBottom: 16, fontSize: 13, color: '#2ecc71' }}>{msg}</div>}
      <div style={{ textAlign: 'right', marginBottom: 10 }}>
        <a href="/ayuda/mi-plan" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, color: 'var(--ink-700)', textDecoration: 'underline' }}>¿Cómo funcionan los planes y los pagos?</a>
      </div>

      {pago && (() => {
        const v = {
          consultando: ['#d4af37', 'Consultando tu pago con Mercado Pago…'],
          pendiente: ['#d4af37', 'Mercado Pago todavía no confirmó el pago. En cuanto lo haga, el cargo se marca solo: no hace falta que pagues de nuevo.'],
          aprobado: ['#2ecc71', `Listo, recibimos tu pago de ${money(pago.total)}. ¡Gracias!`],
          rechazado: ['var(--red-400)', 'El pago no se aprobó y no se te cobró nada. Probá de nuevo, con otra tarjeta o por transferencia.'],
          revisar: ['#d4af37', 'Recibimos tu pago y lo estamos revisando. Te avisamos si hace falta algo.'],
          error: ['var(--red-400)', pago.error || 'No pudimos consultar el pago. Recargá la página en un rato.'],
        }[pago.estado] || ['var(--ink-700)', ''];
        return (
          <div style={{ marginBottom: 18, padding: '12px 16px', borderRadius: 6, border: `1px solid ${v[0]}`, color: v[0], fontSize: 13.5, lineHeight: 1.5, display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ flex: 1 }}>{v[1]}</span>
            <button className="lb-iconbtn" onClick={() => setPago(null)}>✕</button>
          </div>
        );
      })()}

      {/* ── Tu plan ── */}
      <div className="lb-card" style={card}>
        <div className="lb-eyebrow" style={{ marginBottom: 6 }}>Tu plan</div>
        {d.plan.exento ? (
          <>
            <div className="lb-display" style={{ fontSize: 26, color: '#fff' }}>Sin cargo</div>
            <p style={{ fontSize: 13, color: 'var(--ink-700)', margin: '6px 0 0' }}>Tu cuenta no paga por la plataforma y tiene todas las funciones habilitadas.</p>
          </>
        ) : d.plan.en_prueba ? (
          <>
            <div className="lb-display" style={{ fontSize: 26, color: '#fff' }}>Prueba gratis</div>
            <p style={{ fontSize: 13, color: 'var(--ink-700)', margin: '6px 0 0', lineHeight: 1.6 }}>
              Durante la prueba tenés <b style={{ color: '#fff' }}>todas las funciones</b> habilitadas.
              {d.trial && <> Llevás {d.trial.qrUsed} de {d.trial.qrLimit} entradas{d.trial.endsAt ? `, hasta el ${fecha(d.trial.endsAt)}` : ''}.</>}
              {' '}Elegí abajo el plan con el que vas a seguir.
            </p>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <div className="lb-display" style={{ fontSize: 26, color: '#fff' }}>{NOMBRE[actual]}</div>
              <span style={{ fontSize: 13, color: 'var(--ink-700)' }}>
                {planDe(actual)?.abono ? `${money(planDe(actual).abono)} por mes + ` : ''}{money(d.precio_entrada)} por entrada
              </span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-600)', marginTop: 4 }}>{planDe(actual)?.lema}</div>
          </>
        )}

        {d.bonificacion && (
          <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 6, background: 'rgba(46,204,113,0.10)', border: '1px solid rgba(46,204,113,0.35)', fontSize: 13, color: '#2ecc71' }}>
            Bonificado al <b>{d.bonificacion.pct}%</b> · {d.bonificacion.hasta ? `hasta el ${fecha(new Date(new Date(d.bonificacion.hasta).getTime() - 1000))}` : 'sin vencimiento'}
          </div>
        )}

        {pendiente && (
          <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 6, background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.4)', fontSize: 13, color: 'var(--ink-800)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ flex: 1 }}>Pasás a <b style={{ color: '#fff' }}>{NOMBRE[pendiente.plan]}</b> el {fecha(pendiente.desde)}. Hasta ese día seguís con {NOMBRE[actual]}.</span>
            <button className="lb-btn lb-btn--ghost lb-btn--sm" disabled={!!busy} onClick={() => cambiar(actual)}>Cancelar el cambio</button>
          </div>
        )}
      </div>

      {/* ── Lo que viene ── */}
      {!d.plan.exento && !d.plan.en_prueba && (d.abono || d.eventos.length > 0) && (
        <div className="lb-card" style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
            <div className="lb-eyebrow">Lo que viene</div>
            <div style={{ fontSize: 13, color: 'var(--ink-700)' }}>Estimado: <b style={{ color: '#fff', fontSize: 16 }}>{money(d.proximo_total)}</b></div>
          </div>
          <p style={{ fontSize: 12, color: 'var(--ink-600)', margin: '6px 0 4px', lineHeight: 1.5 }}>
            Todavía no se emitió. El abono se cobra al cerrar el período; las entradas de cada evento, dos días después de la fecha, con lo vendido hasta ese momento.
          </p>
          {d.abono && (
            <div style={fila}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#fff', fontSize: 13.5 }}>Abono {NOMBRE[d.abono.plan]}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>{fechaCorta(d.abono.desde)} al {fechaCorta(new Date(new Date(d.abono.hasta).getTime() - 864e5))} · vence el {fecha(d.abono.vence)}</div>
              </div>
              <Importe bruto={d.abono.bruto} neto={d.abono.neto} pct={d.abono.bonif_pct} />
            </div>
          )}
          {d.eventos.map(e => (
            <div key={e.id} style={fila}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#fff', fontSize: 13.5 }}>{e.nombre}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>{fecha(e.fecha)} · {e.entradas} {e.entradas === 1 ? 'entrada' : 'entradas'} × {money(d.precio_entrada)}</div>
              </div>
              <Importe bruto={e.bruto} neto={e.neto} pct={e.bonif_pct} />
            </div>
          ))}
        </div>
      )}

      {/* ── A pagar ── */}
      {pendientes.length > 0 && (
        <div className="lb-card" style={{ ...card, borderColor: pendientes.some(c => c.vencido) ? 'rgba(200,16,46,0.5)' : undefined }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
            <div className="lb-eyebrow">A pagar</div>
            <div style={{ fontSize: 13, color: 'var(--ink-700)' }}>Total: <b style={{ color: '#fff', fontSize: 16 }}>{money(d.adeudado)}</b></div>
          </div>
          {pendientes.map(c => (
            <div key={c.id} style={{ ...fila, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <div style={{ color: '#fff', fontSize: 13.5 }}>{c.concept}</div>
                <div style={{ fontSize: 12, color: c.vencido ? 'var(--red-400)' : 'var(--ink-600)' }}>
                  {c.vencido ? 'Venció' : 'Vence'} el {fecha(c.due_at)}
                  {c.bonif_pct ? ` · bonificado ${Number(c.bonif_pct)}%` : ''}
                </div>
              </div>
              <Importe bruto={c.gross_amount} neto={c.amount} pct={Number(c.bonif_pct) || 0} />
            </div>
          ))}

          {d.mp_disponible && (
            <div style={{ marginTop: 16 }}>
              <button className="lb-btn lb-btn--primary lb-btn--block" disabled={!!busy} onClick={pagarConMp}>
                {busy === 'mp' ? 'Abriendo Mercado Pago…' : `Pagar ${money(d.adeudado)} con Mercado Pago`}
              </button>
              <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 6, textAlign: 'center' }}>Con tarjeta o dinero en cuenta. Se acredita al momento y los cargos quedan pagados solos.</div>
            </div>
          )}

          {/* El pago es solo por Mercado Pago: se acredita y se concilia solo. Si la plataforma
              todavía no lo tiene configurado, queda coordinar a mano. */}
          {!d.mp_disponible && (
            <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--ink-700)' }}>Escribinos para coordinar el pago.</div>
          )}
        </div>
      )}

      {/* ── Planes ── */}
      {!d.plan.exento && (
        <>
          <div className="lb-eyebrow" style={{ margin: '28px 0 12px' }}>{d.plan.en_prueba ? 'Elegí tu plan' : 'Cambiar de plan'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 14, marginBottom: 10 }}>
            {d.planes.map(p => {
              const esActual = !d.plan.en_prueba && p.key === actual;
              const esPendiente = pendiente?.plan === p.key;
              const sube = ORDEN.indexOf(p.key) > ORDEN.indexOf(actual);
              return (
                <div key={p.key} className="lb-card" style={{ padding: '18px 18px 16px', display: 'flex', flexDirection: 'column', border: esActual ? '1.5px solid #EC268F' : undefined }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div className="lb-h" style={{ fontSize: 18 }}>{p.nombre}</div>
                    {esActual && <span style={{ fontSize: 10.5, letterSpacing: '0.08em', color: '#EC268F', fontWeight: 700 }}>TU PLAN</span>}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <span style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{p.abono ? money(p.abono) : 'Sin abono'}</span>
                    {p.abono > 0 && <span style={{ fontSize: 12, color: 'var(--ink-600)' }}> / mes</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>+ {money(d.precio_entrada)} por entrada</div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 14px', fontSize: 12.5, color: 'var(--ink-800)', lineHeight: 1.7, flex: 1 }}>
                    {p.incluye.map(x => <li key={x}><span style={{ color: '#2ecc71', marginRight: 6 }}>✓</span>{x}</li>)}
                  </ul>
                  {esActual ? (
                    <button className="lb-btn lb-btn--ghost lb-btn--sm lb-btn--block" disabled>Es tu plan actual</button>
                  ) : esPendiente ? (
                    <button className="lb-btn lb-btn--ghost lb-btn--sm lb-btn--block" disabled>Pasás el {fechaCorta(pendiente.desde)}</button>
                  ) : (
                    <button className={'lb-btn lb-btn--sm lb-btn--block ' + (d.plan.en_prueba || sube ? 'lb-btn--primary' : 'lb-btn--ghost')} disabled={!!busy} onClick={() => cambiar(p.key)}>
                      {busy === p.key ? '…' : d.plan.en_prueba ? `Elegir ${p.nombre}` : `Pasar a ${p.nombre}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {!d.plan.en_prueba && (
            <p style={{ fontSize: 12, color: 'var(--ink-600)', lineHeight: 1.6, margin: '0 0 24px' }}>
              Subir de plan es inmediato: lo que va del período con el plan anterior se cobra proporcional a los días.
              Bajar rige al cierre del período que ya estás pagando, así no perdés lo que pagaste.
            </p>
          )}
        </>
      )}

      {/* ── Historial ── */}
      {historial.length > 0 && (
        <div className="lb-card" style={card}>
          <div className="lb-eyebrow">Historial</div>
          {historial.map(c => (
            <div key={c.id} style={{ ...fila, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: 'var(--ink-800)', fontSize: 13 }}>{c.concept}</div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-600)' }}>
                  {c.status === 'bonificado' ? 'Bonificado' : `Pagado el ${fecha(c.paid_at)}${c.pagado_por === 'mercadopago' ? ' con Mercado Pago' : ''}`}
                </div>
              </div>
              <Importe bruto={c.gross_amount} neto={c.amount} pct={Number(c.bonif_pct) || 0} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
